import { defineComponent as _defineComponent } from 'vue'
import CountrySwitcherModal from '@/features/buybox/components/CountrySwitcherModal.vue';
import { useI18n } from '@/helpers/composables/useI18n';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { BuyboxPresentationType, useUserStore } from '@/stores/pinia/useUserStore';
import type { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';
import { ModalHelper } from '@/helpers/vfm/modal-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { PresentationType, MonetizationType } from '@/@types/graphql-types';
import { PresentationTypes } from '@/interfaces/titles';
import ChipButton from '@/ui-components/ChipButton.vue';
import ChipButtons from '@/ui-components/ChipButtons.vue';
import { useRoute } from 'vue-router/composables';
import { computed, onMounted, ref } from 'vue';
import { faAngleDown, faFilter } from '@fortawesome/free-solid-svg-icons';


export default /*#__PURE__*/_defineComponent({
  __name: 'GridBuyBoxFilter',
  props: {
    isInModal: { type: Boolean },
    defaultPresentationType: { default: null },
    additionalContexts: { default: () => [] },
    isEpisodeBuybox: { type: Boolean },
    episodeBuyBoxPresentationType: { default: null }
  },
  emits: ["episodeFilterChange"],
  setup(__props: any, { emit: emits }: { emit: ({
	(e: 'episodeFilterChange', presentationType: BuyboxPresentationType | null): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { isInModal?: boolean, defaultPresentationType: BuyboxPresentationType | null, additionalContexts: SnowplowContext[], isEpisodeBuybox?: boolean, episodeBuyBoxPresentationType: BuyboxPresentationType | null };




const languageStore = useLanguageStore();
const userStore = useUserStore();

const route = useRoute();

const { tc } = useI18n();
const filterOptions = computed(() => [
	{
		label: tc('WEBAPP_QUALITY_BESTPRICE'),
		value: null,
		isQuality: false,
	},
	{
		label: tc('WEBAPP_BUYBOX_FREE_FILTER'),
		value: MonetizationType.Free,
		isQuality: false,
	} as const,
	...PresentationTypes.filter(g => g.isQuality),
]);

function openCountrySwitcherModal() {
	ModalHelper.openModal(CountrySwitcherModal, { closable: true });
	TrackingHelper.trackEvent('userinteraction', { action: 'country_selector_clicked' }, props.additionalContexts);
}

async function handleFilterClick(payload: {
	presentationType: PresentationType | MonetizationType.Free | null;
	additionalContexts?: SnowplowContext[];
}) {
	// updating the buybox is expensive.
	// that's why we split it up in 1) optimistic UI update
	if (!props.isEpisodeBuybox) {
		userStore.setBuyboxFilterTypeOptimistic(payload.presentationType);
	} else {
		emits('episodeFilterChange', payload.presentationType);
	}
	// then 2) give the JS thread time to render the optimistic UI update immediately
	await new Promise(resolve => setTimeout(resolve, 0));
	// and 3) defer the expensive store commit to the end
	if (!props.isEpisodeBuybox) {
		userStore.setBuyboxPresentationType(payload);
	}
}

const _buyboxFilter = ref<undefined | null | PresentationType>(undefined);
const buyboxFilter = computed(() => {
	if (props.isEpisodeBuybox) {
		return props.episodeBuyBoxPresentationType;
	}
	// we need an additional state because buyboxFilterTypeOptimistic's `null` can mean multiple things:
	// - user hasn't filtered anything
	// - user has filtered on "BEST"
	if (_buyboxFilter.value !== undefined) {
		return _buyboxFilter.value;
	}
	return userStore.buyboxFilterTypeOptimistic !== null
		? userStore.buyboxFilterTypeOptimistic
		: userStore.buyboxPresentationType;
});

onMounted(() => {
	// for Apple TV+ search ads
	if (route.query.monetization_types === 'free') {
		return userStore.setBuyboxPresentationType({ presentationType: MonetizationType.Free });
	}

	if (!props.defaultPresentationType) return;

	userStore.setBuyboxPresentationType({
		presentationType: props.defaultPresentationType,
		additionalContexts: props.additionalContexts,
	});
});

return { __sfc: true,props, emits, languageStore, userStore, route, tc, filterOptions, openCountrySwitcherModal, handleFilterClick, _buyboxFilter, buyboxFilter, ChipButton, ChipButtons, faAngleDown, faFilter }
}

})